import { useState } from 'react';
import { LIMITS, slugify, type MeResponse } from '@stonelink/shared';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Button, ErrorNote, Eyebrow, Input, Label } from '../ui';

/**
 * Self-serve signup. This is the gate in front of the install snippet: you get
 * a workspace, and the workspace is what the tag identifies.
 */
export function SignupPage({ onSignedIn }: { onSignedIn: (session: MeResponse) => void }) {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Honeypot: hidden from people, irresistible to form-filling bots.
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shown live because this ends up in the customer's embed tag — it should
  // not be a surprise after the fact.
  const slug = slugify(businessName);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.signup({ businessName, email, password, website }));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not create your account just now.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form onSubmit={(event) => void submit(event)} className="glass w-full max-w-md p-8">
        <div className="mb-7">
          <Eyebrow>Stonelink Assist</Eyebrow>
          <h1 className="font-display text-4xl leading-none">
            Create your <em className="text-brand italic">workspace.</em>
          </h1>
          <p className="mt-3 text-sm text-soft">
            You will land straight in the console, with your install tag ready.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="businessName">Business name</Label>
            <Input
              id="businessName"
              required
              autoComplete="organization"
              maxLength={LIMITS.tenantName}
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
            />
            <p className="mt-2 min-h-[1.25rem] font-mono text-[11px] tracking-[0.1em] text-muted">
              {slug ? `Your workspace ID will be ${slug}` : ''}
            </p>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={LIMITS.password.min}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <p className="mt-2 text-xs text-muted">At least {LIMITS.password.min} characters.</p>
          </div>

          <div aria-hidden="true" className="absolute -left-[9999px]">
            <label htmlFor="website">Leave this empty</label>
            <input
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </Button>

          <p className="text-center text-sm text-muted">
            Already have one?{' '}
            <Link to="/" className="text-brand hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
